import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const ChevronDown = ({ className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="30" height="16" viewBox="0 0 30 16" fill="none" className={className}>
    <path d="M1 1L15 15L29 1" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
  </svg>
);

// Responsive image with AVIF + WebP sources. AVIF preserves dark gradient
// content; WebP is the fallback for older Safari/Edge.
// `name` corresponds to bases in /assets/optimized/, e.g. "panel_1" ->
// panel_1-700.{avif,webp}, panel_1-1220.{avif,webp}.
// Newer renders only ship WebP; pass exts={['webp']} for those.
const Picture = ({
  name,
  sizes,
  widths,
  exts = ['avif', 'webp'],
  alt = '',
  className = '',
  priority = false,
}) => {
  const buildSrcSet = (ext) =>
    widths.map((w) => `/assets/optimized/${name}-${w}.${ext} ${w}w`).join(', ');
  const fallback = `/assets/optimized/${name}-${widths[widths.length - 1]}.webp`;
  return (
    <picture style={{ display: 'contents' }}>
      {exts.map((ext) => (
        <source key={ext} type={`image/${ext}`} srcSet={buildSrcSet(ext)} sizes={sizes} />
      ))}
      <img
        src={fallback}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        decoding={priority ? 'sync' : 'async'}
        fetchpriority={priority ? 'high' : 'auto'}
        className={className}
      />
    </picture>
  );
};

// Heading (Geist Regular) right-aligned against the rule; body copy is
// Geist Light. Single column on mobile, two columns capped at 1200px on
// desktop. font-[300] instead of font-light because the Tailwind config's
// `light` fontFamily alias shadows the weight utility.
const TextSection = ({ heading, paragraphs }) => (
  <section className="w-full px-[12px] py-[50px] md:p-[100px]">
    <div className="flex flex-col gap-[6px] md:mx-auto md:w-[1200px] md:max-w-full md:flex-row md:gap-[50px] md:items-start">
      <div className="flex w-full flex-col gap-[6px] md:flex-1 md:gap-[10px]">
        <h2 className="text-right text-[16px] leading-[18px] md:text-[32px] md:leading-[36px]">
          {heading}
        </h2>
        <div className="h-px w-full bg-[#489BFF]/25" />
      </div>
      <div className="flex flex-col gap-[6px] font-[300] text-[16px] leading-[18px] md:flex-1 md:gap-[10px] md:text-[20px] md:leading-[24px]">
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </div>
  </section>
);

// Full-bleed 750px render band. New renders are WebP-only.
const RenderSection = ({ name, bg = '#1E1E1E', exts = ['webp'] }) => (
  <section className="h-[750px] w-full overflow-hidden" style={{ backgroundColor: bg }}>
    <Picture
      name={name}
      widths={[1280, 1920, 3840]}
      exts={exts}
      sizes="100vw"
      alt=""
      className="h-full w-full object-cover"
    />
  </section>
);

// sizes attributes describe how wide the image will render so the browser
// can pick the right srcset entry. They reflect the grid layouts below.
const PANEL_SIZES = '(min-width: 912px) 25vw, 50vw';

const Home = () => {
  useEffect(() => {
    const id = 'geist-font';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);

  return (
    <div className="flex w-full max-w-full flex-col overflow-x-hidden bg-[#111111] text-[#489BFF] font-['Geist',sans-serif]">
      {/* Hero */}
      <section className="relative h-svh w-full overflow-hidden bg-[#1E1E1E]">
        <Picture
          name="hero_2"
          widths={[1280, 1920, 3840]}
          sizes="100vw"
          alt=""
          priority
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute left-1/2 top-[45px] -translate-x-1/2 font-brand text-[48px] leading-[1.32] tracking-[0.96px] md:top-[25px]">
          oldholy
        </div>
        <ChevronDown className="absolute bottom-[34px] left-1/2 -translate-x-1/2 md:bottom-[42px]" />
      </section>

      <TextSection
        heading="This is Black Box."
        paragraphs={[
          'In the future, computers won’t be for email or spreadsheets or scheduling. They’ll be instruments for understanding how the world works.',
          'Introducing infinity in a box.',
        ]}
      />

      {/* Panel grid: 2x2 on mobile, 4 across on desktop */}
      <section className="grid h-[600px] w-full grid-cols-2 gap-px overflow-hidden md:grid-cols-4">
        {['panel_2', 'panel_1', 'panel_4', 'panel_6'].map((name) => (
          <div key={name} className="overflow-hidden bg-[#1E1E1E]">
            <Picture
              name={name}
              widths={[700, 1220]}
              sizes={PANEL_SIZES}
              alt=""
              className="h-full w-full object-cover"
            />
          </div>
        ))}
      </section>

      <TextSection
        heading="You’re a tiny explorer."
        paragraphs={[
          'Inside Black Box is a miniature reality meant to be observed, experienced, and gently prodded.',
          'Imagine an aquarium containing an entire planet, asking little of your attention while rewarding every minute you put into it.',
        ]}
      />

      <RenderSection name="side_far" />

      <TextSection
        heading="This is the future of computing."
        paragraphs={[
          'Video games, media, and personal computers have been converging on the same thing for decades: a simple tool to explore the infinite possible.',
          'Discover what it means to have a whole world on your desk – a place where you can try anything and see how it plays out.',
        ]}
      />

      <RenderSection name="front_off_center" />

      <TextSection
        heading="Designed to work like nature."
        paragraphs={[
          'The world inside Black Box runs perpetually, reshaping itself over time while keeping narrative continuity.',
          'Burn it to the ground and it will grow back again as something entirely new – with the threads of its own history woven in.',
        ]}
      />

      {/* ASCII terrain map (transparent webp) on white */}
      <section className="h-[750px] w-full overflow-hidden bg-[#FFFFFF]">
        <img
          src="/assets/optimized/ascii_map_3-2782.webp"
          srcSet="/assets/optimized/ascii_map_3-1391.webp 1391w, /assets/optimized/ascii_map_3-2782.webp 2782w"
          sizes="100vw"
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      </section>

      <TextSection
        heading="Experience a distributed OS."
        paragraphs={[
          'Every device contains a distributed network of thousands of independent operators that are constantly perceiving and learning from each other, adapting and changing with each interaction.',
          'You can’t control them, pause them, or edit them; you can only convince them to collaborate.',
          'We urge you not to delete them.',
        ]}
      />

      <RenderSection name="side_close" />

      <TextSection
        heading="It’s about the feeling."
        paragraphs={[
          'Black Box represents a calmer, more ambient vision for the future of personal computing.',
          'We see a future for interfaces that are passive, simple and enjoyable, with less emphasis on consuming attention and driving transactions and more on making you feel like a person again.',
        ]}
      />

      <footer className="flex w-full flex-col items-center gap-[54px] px-[16px] py-[40px]">
        <div className="font-brand text-[36px] leading-[1.32] tracking-[0.72px]">oldholy</div>
        <div className="flex flex-col items-center gap-[20px] text-center text-[15px] font-[300]">
          <Link to="/privacy" className="hover:underline">
            Privacy policy
          </Link>
          <Link to="/terms" className="hover:underline">
            Terms of use
          </Link>
        </div>
        <div className="text-center text-[15px] font-[300] text-[#489BFF]/25">
          © 2026 Worldlines Technology
        </div>
      </footer>
    </div>
  );
};

export default Home;
